import React, { useEffect, useState } from "react";
import { Worker } from "../../types/worker.type";
import { api } from "../../api/api";
import {
  Button,
  Dropdown,
  Form,
  Input,
  InputNumber,
  Menu,
  message,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Typography,
  DatePicker,
} from "antd";
import { DownOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { renderTime } from "../../utils/renderTime";
import { ISalary, SalaryType } from "../../types/salary.type";
import { Currency } from "../../types/currency.type";
import { PiMicrosoftExcelLogoFill } from "react-icons/pi";
import { useDownloadFile } from "../../hooks/useDownloadFile";
import { useAuth } from "../../contexts/auth.context";
import { UserRole } from "../../types/user.type";
import { canEditWorkers } from "../../utils/roleUtils";
import dayjs from "dayjs";
import { positionApi } from '../../api/positionApi';
import { Position } from '../../types/position.type';
import { useQueryParams } from "../../hooks/useQueryParams";

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;

export default function Workers() {
  const { authState } = useAuth();
  const navigate = useNavigate();
  const queryParams = useQueryParams();
  
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  
  // Инициализация состояний из URL query параметров
  const [page, setPage] = useState<number>(queryParams.getParamAsNumber("page", 1));
  const [pageSize, setPageSize] = useState<number>(queryParams.getParamAsNumber("pageSize", 10));
  const [total, setTotal] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>(queryParams.getParam("search") || "");
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const [form] = Form.useForm();
  const [reportForm] = Form.useForm();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isPerDayReportModalOpen, setIsPerDayReportModalOpen] = useState(false);
  const [salary, setSalary] = useState<ISalary | null>(null);
  const [salaryLoading, setSalaryLoading] = useState(false);
  const [isNormModalOpen, setIsNormModalOpen] = useState<boolean>(false);
  const [norm, setNorm] = useState<number | null>(null);
  const { loading: downloadLoading, downloadFile } = useDownloadFile();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingWorker, setEditingWorker] = useState<Worker | null>(null);
  const [editForm] = Form.useForm();
  const [isEditSalaryOpen, setIsSalaryOpen] = useState<boolean>(false);
  const [salaryType, setSalaryType] = useState<SalaryType | null>(null);
  const [workerId, setWorkerId] = useState<string | null>(null);
  const [positions, setPositions] = useState<Position[]>([]);
  const [selectedPositionId, setSelectedPositionId] = useState<string | null>(null);
  const [addType, setAddType] = useState<'shift' | 'additional'>('shift');
  const [selectedShift, setSelectedShift] = useState<string | null>(null);


  const fetchWorkers = async () => {
    setLoading(true);
    try {
      const response = await api.get("/worker", {
        params: {
          page,
          limit: pageSize,
          search: searchTerm,
        },
      });
      setWorkers(response.data.workers);
      setTotal(response.data.total);
    } catch (error) {
      console.error("Error fetching workers", error);
      message.error("Ошибка получения сотрудников.");
    } finally {
      setLoading(false);
    }
  };

  const fetchPricing = async (workerId: string) => {
    setSalaryLoading(true);
    try {
      const response = await api.get(`/salary/worker/${workerId}`);

      setSalary(response.data.salary);
    } catch (error) {
      console.error("Error fetching salary", error);
      message.error("Ошибка получение записи.");
    } finally {
      setSalaryLoading(false);
    }
  };

  const handleDeleteWorker = async (workerId: string) => {
    try {
      await api.delete(`/worker/${workerId}`);
      message.success("Сотрудник успешно удален.");
      fetchWorkers();
    } catch (error: any) {
      console.error("Error deleting worker", error);
      message.error(error.response.data.error);
    }
  };

  const openAddModal = async () => {
    setIsModalOpen(true);
    try {
      const pos = await positionApi.getPositions();
      setPositions(pos);
    } catch (e) {
      message.error('Ошибка загрузки должностей');
    }
  };

  const handleSubmitForm = async (values: Partial<Worker>) => {
    setConfirmLoading(true);
    try {
      // 1. Создать сотрудника
      const res = await api.post("/worker/", values);
      const newWorker = res.data.worker;
      // 2. Если выбрана должность — добавить сотрудника в смену или в доп. работники
      if (selectedPositionId) {
        const pos = positions.find(p => p._id === selectedPositionId);
        if (pos) {
          let update: any = {};
          // Универсальная функция для фильтрации и преобразования к строке id
          const toIdArray = (arr: any[]): string[] =>
            arr
              .map(w => (typeof w === 'object' && w !== null ? w._id : w))
              .filter((id): id is string => Boolean(id));

          if (addType === 'shift' && selectedShift) {
            update.shifts = pos.shifts.map(shift =>
              shift.shiftName === selectedShift
                ? {
                    ...shift,
                    workerIds: [
                      ...toIdArray(shift.workerIds),
                      newWorker._id
                    ]
                  }
                : {
                    ...shift,
                    workerIds: toIdArray(shift.workerIds)
                  }
            );
            update.additionalWorkers = toIdArray(pos.additionalWorkers);
          } else if (addType === 'additional') {
            update.shifts = pos.shifts.map(shift => ({
              ...shift,
              workerIds: toIdArray(shift.workerIds)
            }));
            // Исключаем дублирование id
            const currentIds = toIdArray(pos.additionalWorkers);
            update.additionalWorkers = currentIds.includes(newWorker._id)
              ? currentIds
              : [...currentIds, newWorker._id];
          }
          await positionApi.updatePosition(pos._id, update);
        }
      }
      message.success("Сотрудник успешно создан.");
      fetchWorkers();
      setIsModalOpen(false);
      setConfirmLoading(false);
      form.resetFields();
      setSelectedPositionId(null);
      setAddType('shift');
      setSelectedShift(null);
    } catch (error: any) {
      setConfirmLoading(false);
      console.error("Error adding worker", error);
      message.error(error.response?.data?.error || 'Произошла ошибка');
    }
  };

  const handleUpdateSalary = async () => {
    if (workerId && salaryType !== null) {
      setConfirmLoading(true);
      try {
        const response = await api.put(`/worker/salary-type/${workerId}/`, {
          type: salaryType,
        });
        message.success(response.data.message);
        fetchWorkers();
        setIsSalaryOpen(false);
        setConfirmLoading(false);
      } catch (error: any) {
        console.error("Error updating salary type", error);
        message.error(error.response.data.error);
        setConfirmLoading(false);
      }
    }
  };


  const handleReportOpen = (workerId: string, type: SalaryType) => {
    if (type === SalaryType.PERDAY) {
      setIsPerDayReportModalOpen(true);
      setWorkerId(workerId);
    } else {
      setIsReportModalOpen(true);
      fetchPricing(workerId);
    }
  };

  const handleReportCreate = async (values: any) => {
    const details = (salary?.pricings ?? []).map((pricing) => {
      return {
        pricingId: pricing._id,
        dayQuantity: values[`${pricing._id}_day_quantity`],
        nightQuantity: values[`${pricing._id}_night_quantity`],
        wasteQuantity: values[`${pricing._id}_waste_quantity`],
      };
    });

    setConfirmLoading(true);

    try {
      await api.post(`/salary/worker/${salary?.workerId}/report`, {
        yuk: values.yuk,
        details,
      });

      message.success("Отчет успешно создан.");
      fetchWorkers();
      setIsReportModalOpen(false);
      setConfirmLoading(false);
      reportForm.resetFields();
    } catch (error: any) {
      setConfirmLoading(false);
      console.error("Error creating report", error);
      message.error(error.response.data.error);
    }
  };

  const createPerDayReport = async (workerId: string) => {
    setConfirmLoading(true);

    try {
      await api.post(`/salary/per-day/report/${workerId}`);
      message.success("Отчет успешно создан.");
      fetchWorkers();
    } catch (error: any) {
      setConfirmLoading(false);
      console.error("Error creating report", error);
      message.error(error.response.data.error);
    } finally {
      setConfirmLoading(false);
      setIsPerDayReportModalOpen(false);
    }
  };

  const handleEditWorker = async (values: Partial<Worker>) => {
    if (!editingWorker) return;

    setConfirmLoading(true);
    try {
      await api.put(`/worker/${editingWorker._id}`, values);
      message.success("Данные сотрудника успешно обновлены.");
      fetchWorkers();
      setIsEditModalOpen(false);
      setEditingWorker(null);
      editForm.resetFields();
    } catch (error: any) {
      console.error("Error updating worker", error);
      message.error(error.response?.data?.error || "Ошибка обновления данных сотрудника");
    } finally {
      setConfirmLoading(false);
    }
  };

  // Синхронизация состояний с URL query параметрами
  useEffect(() => {
    const params: Record<string, string | number | boolean | undefined> = {};
    
    if (searchTerm) params.search = searchTerm;
    if (page !== 1) params.page = page;
    if (pageSize !== 10) params.pageSize = pageSize;
    
    queryParams.setParams(params);
  }, [searchTerm, page, pageSize]);

  useEffect(() => {
    fetchWorkers();
  }, [page, pageSize, searchTerm]);

  const handleExport = () => {
    downloadFile("/export/workers", "workers_data.xlsx");
  };

  const columns = [
    {
      title: "Имя",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Фамилия",
      dataIndex: "surname",
      key: "surname",
    },
    {
      title: "Прозвище",
      dataIndex: "nickname",
      key: "nickname",
    },
    {
      title: "Имя пользователя",
      dataIndex: "username",
      key: "username",
    },
    {
      title: "Тип зарплаты",
      dataIndex: "salaryType",
      key: "salaryType",
    },
    {
      title: "Валюта зарплаты",
      dataIndex: "salaryCurrency",
      key: "salaryCurrency",
    },
    {
      title: "Станки",
      key: "machines",
      render: (_: any, worker: Worker) => (
        <ol>
          {worker.machines.map((item) => (
            <li key={item.machineId}>{item.machineName}</li>
          ))}
        </ol>
      ),
    },
    {
      title: "",
      key: "actions",
      render: (_: any, worker: Worker) => {
        const menu = (
          <Menu>
            <Menu.Item
              onClick={() => {
                const name = `${worker.name} ${worker.surname}`;
                navigate(`/${worker._id}/${name}/salary/salary-reports`);
              }}
            >
              Смотреть отчеты
            </Menu.Item>

            {(authState.user?.role === UserRole.Admin ||
              authState.user?.role === UserRole.Accountant || authState.user?.role === UserRole.Cashier || authState.user?.role === UserRole.Director) && (
                <>
                  <Menu.Item
                    onClick={() =>
                      handleReportOpen(worker._id, worker.salaryType)
                    }
                  >
                    Оставить отчет
                  </Menu.Item>
                </>
              )}

            {(authState.user?.role === UserRole.Admin || authState.user?.role === UserRole.Accountant || authState.user?.role === UserRole.Cashier || authState.user?.role === UserRole.Director) && (
              <>
                <Menu.Item
                  onClick={() => {
                    setEditingWorker(worker);
                    editForm.setFieldsValue({
                      name: worker.name,
                      surname: worker.surname,
                      nickname: worker.nickname,
                    });
                    setIsEditModalOpen(true);
                  }}
                >
                  Редактировать
                </Menu.Item>
                <Popconfirm
                  title="Вы уверены, что хотите удалить этого сотрудника?"
                  okText="Да"
                  cancelText="Нет"
                  onConfirm={() => handleDeleteWorker(worker._id)}
                >
                  <Menu.Item>Удалить</Menu.Item>
                </Popconfirm>

                <Menu.Item
                  onClick={() => {
                    setWorkerId(worker._id);
                    setSalaryType(worker.salaryType);
                    setIsSalaryOpen(true);
                  }}
                >
                  Изменить зарплату
                </Menu.Item>
              </>
            )}
          </Menu>
        );

        return (
          <Dropdown overlay={menu}>
            <Button type="link">
              Действия <DownOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  return (
    <div style={{ padding: 24 }}>
      <Title level={2}>Сотрудники</Title>
      <Space style={{ marginBottom: 16 }}>
        <Search placeholder="Поиск" onSearch={handleSearch} allowClear />
        {canEditWorkers(authState.user?.role) && (
            <Button type="primary" onClick={openAddModal}>
              Добавить
            </Button>
          )}
        <Button
          type="default"
          loading={downloadLoading}
          icon={<PiMicrosoftExcelLogoFill />}
          onClick={handleExport}
        >
          Экспорт в Excel
        </Button>
      </Space>
      <Table
        columns={columns}
        dataSource={workers}
        loading={loading}
        pagination={{
          current: page,
          total: total,
          pageSize: pageSize,
          onChange: (page, size) => {
            setPage(page);
            if (size !== pageSize) setPageSize(size!);
          },
          showSizeChanger: true,
        }}
        rowKey="_id"
        scroll={{ x: "max-content" }}
      />

      <Modal
        title="Добавить сотрудника"
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setSelectedPositionId(null);
          setAddType('shift');
          setSelectedShift(null);
        }}
        onOk={() => form.submit()}
        confirmLoading={confirmLoading}
      >
        <Form form={form} onFinish={handleSubmitForm} layout="vertical">
          <Form.Item
            name="name"
            label="Введите имя сотрудника"
            rules={[{ required: true, message: "Введите имя сотрудника" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="surname"
            label="Введите фамилию сотрудника"
            rules={[{ required: true, message: "Введите фамилию сотрудника" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="nickname"
            label="Введите прозвище сотрудника (необязательно)"
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="salaryType"
            label="Выберите тип зарплаты"
            rules={[{ required: true, message: "Выберите тип зарплаты" }]}
          >
            <Select placeholder="Тип зарплаты">
              {Object.values(SalaryType).map((type) => (
                <Option key={type} value={type}>
                  {type}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="salaryCurrency"
            label="Выберите валюту зарплаты"
            rules={[{ required: true, message: "Выберите валюту зарплаты" }]}
          >
            <Select placeholder="Валюта зарплаты">
              {Object.values(Currency).map((currency) => (
                <Option key={currency} value={currency}>
                  {currency}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="positionId"
            label="Должность"
            rules={[{ required: true, message: "Выберите должность" }]}
          >
            <Select
              showSearch
              placeholder="Выберите должность"
              onChange={val => {
                setSelectedPositionId(val);
                setAddType('shift');
                setSelectedShift(null);
                form.setFieldsValue({ addType: 'shift', shiftName: undefined });
              }}
              optionFilterProp="children"
            >
              {positions.map(pos => (
                <Option key={pos._id} value={pos._id}>{pos.name}</Option>
              ))}
            </Select>
          </Form.Item>
          {selectedPositionId && (
            <Form.Item 
              name="addType"
              label="Куда добавить сотрудника" 
              rules={[{ required: true, message: "Выберите вариант" }]}
              initialValue="shift"
            >
              <Select
                onChange={val => {
                  setAddType(val);
                  if (val === 'additional') {
                    form.setFieldsValue({ shiftName: undefined });
                  }
                }}
                style={{ width: 220 }}
              >
                <Option value="shift">В смену</Option>
                <Option value="additional">В дополнительные работники</Option>
              </Select>
            </Form.Item>
          )}
          {selectedPositionId && addType === 'shift' && (
            <Form.Item 
              name="shiftName"
              label="Смена" 
              rules={[{ required: true, message: "Выберите смену" }]}
            >
              <Select
                onChange={val => setSelectedShift(val)}
                placeholder="Выберите смену"
              >
                {positions.find(p => p._id === selectedPositionId)?.shifts.map(shift => (
                  <Option key={shift.shiftName} value={shift.shiftName}>{shift.shiftName}</Option>
                ))}
              </Select>
            </Form.Item>
          )}
        </Form>
      </Modal>

      <Modal
        title="Отчет"
        open={isReportModalOpen}
        onCancel={() => setIsReportModalOpen(false)}
        onOk={() => reportForm.submit()}
        confirmLoading={confirmLoading}
        loading={salaryLoading}
      >
        <Form form={reportForm} layout="vertical" onFinish={handleReportCreate}>
          <Form.Item name="yuk" label="Количество Юк">
            <InputNumber defaultValue={0} />
          </Form.Item>
          {salary &&
            salary.pricings.map((pricing) => (
              <>
                <div>{pricing.forName}</div>
                <Form.Item
                  name={`${pricing._id}_day_quantity`}
                  label="Количество днём"
                >
                  <InputNumber min={0} />
                </Form.Item>
                <Form.Item
                  name={`${pricing._id}_night_quantity`}
                  label="Количество ночью"
                >
                  <InputNumber min={0} />
                </Form.Item>
                <Form.Item
                  name={`${pricing._id}_waste_quantity`}
                  label="Количество отхода"
                >
                  <InputNumber min={0} />
                </Form.Item>
              </>
            ))}
        </Form>
      </Modal>

      <Modal
        title="Отчет за день"
        open={isPerDayReportModalOpen}
        onCancel={() => setIsPerDayReportModalOpen(false)}
        onOk={() => createPerDayReport(workerId!)}
        confirmLoading={confirmLoading}
      >
        <Form layout="vertical">
          <Form.Item name="quantity" label="Кол-во">
            <InputNumber defaultValue={1} disabled />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Редактировать сотрудника"
        open={isEditModalOpen}
        onCancel={() => {
          setIsEditModalOpen(false);
          setEditingWorker(null);
          editForm.resetFields();
        }}
        onOk={() => editForm.submit()}
        confirmLoading={confirmLoading}
      >
        <Form form={editForm} onFinish={handleEditWorker} layout="vertical">
          <Form.Item
            name="name"
            label="Имя сотрудника"
            rules={[{ required: true, message: "Введите имя сотрудника" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="surname"
            label="Фамилия сотрудника"
            rules={[{ required: true, message: "Введите фамилию сотрудника" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="nickname"
            label="Прозвище сотрудника (необязательно)"
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Изменить тип зарплаты"
        open={isEditSalaryOpen}
        onCancel={() => setIsSalaryOpen(false)}
        onOk={handleUpdateSalary}
        confirmLoading={confirmLoading}
      >
        <Select
          placeholder="Тип зарплаты"
          value={salaryType}
          onChange={(type) => setSalaryType(type)}
        >
          {Object.values(SalaryType).map((type) => (
            <Option key={type} value={type}>
              {type}
            </Option>
          ))}
        </Select>
      </Modal>
    </div>
  );
}
